<template>
  <div class="w-full grid lg:grid-cols-10 md:grid-cols-8 sm:grid-cols-4 grid-cols-2 gap-4 items-end border-b-2 border-[#4C535966]" v-if="!isGroup || !pax.paxId">
    <template v-for="(field, i) of activeFields" :key="i">
      <div v-if="!field.show || field.show()" class="mb-5" :class="field.wrapperClass || `col-span-${field.span || 2}`">
        <label v-if="field.special !== 'file'" class="block mb-2 text-sm font-medium text-gray uppercase">
          {{ tr(field.label) }}<span v-if="field.required" class="ml-2 text-primary">*</span>
        </label>

        <template v-if="field.special === 'paxSelect'">
          <select v-model="pax.selected" @change="update" :class="{'error': errors.selected, 'readonly-field': isDisabled}"
            :disabled="isDisabled && pax.selected" class="bg-light-gray border border-[#ADAEAE] text-gray text-sm rounded-lg focus:border-[#ADAEAE] focus:ring-transparent outline-none block w-full p-3">
            <option v-if="pax.selected" :value="pax.selected">{{ pax.selected }}</option>
            <option v-for="(p, idx) of paxList" :key="idx" :value="p">{{ p }}</option>
          </select>
        </template>

        <template v-else-if="field.special === 'date'">
          <div class="relative w-full">
            <DatePartsSelect
              :value="pax[field.key]"
              :disabled="isDisabled"
              :error="errors[field.key]"
              :minYear="field.minYear || 1900"
              :maxYear="field.maxYear || new Date().getFullYear()"
              @input="(value) => updateDateField(field.key, value)"
            />
          </div>
        </template>

        <template v-else-if="field.special === 'file'">
          <div class="inline-block">
            <label :for="`file-upload-${paxNumber}`" class="cursor-pointer pb-2 border-b border-black uppercase font-semibold text-sm flex w-auto">
              <svg width="26" height="12" viewBox="0 0 26 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.5303 0.96967C13.2374 0.676777 12.7626 0.676777 12.4697 0.96967L7.6967 5.74264C7.40381 6.03553 7.40381 6.51041 7.6967 6.8033C7.98959 7.09619 8.46447 7.09619 8.75736 6.8033L13 2.56066L17.2426 6.8033C17.5355 7.09619 18.0104 7.09619 18.3033 6.8033C18.5962 6.51041 18.5962 6.03553 18.3033 5.74264L13.5303 0.96967ZM13.75 11.5L13.75 1.5L12.25 1.5L12.25 11.5L13.75 11.5Z" fill="#1F2326"/>
              </svg>
              {{ tr(field.label) }}
            </label>
            <input :id="`file-upload-${paxNumber}`" class="hidden" type="file" @change="uploadPassport($event)" multiple />
            <div v-for="(file, fi) of pax.files" :key="fi">
              {{ file.name }}
            </div>
          </div>
        </template>

        <template v-else-if="field.type === 'select'">
          <select v-model="pax[field.key]" @change="update" :id="field.id" :disabled="isDisabled"
            :class="{'error': errors[field.key], 'readonly-field': isDisabled}"
            class="bg-light-gray border border-[#ADAEAE] text-gray text-sm rounded-lg focus:border-[#ADAEAE] focus:ring-transparent outline-none block w-full p-3">
            <template v-if="field.options === 'gender'">
              <option :selected="pax[field.key] == 'MALE'" value="MALE">{{ tr("Male") }}</option>
              <option :selected="pax[field.key] == 'FEMALE'" value="FEMALE">{{ tr("Female") }}</option>
            </template>
            <template v-else-if="field.options === 'countries'">
              <option v-for="(c, ci) of countriesJSON" :key="ci" :value="c.alpha2" :selected="c.alpha2 == pax[field.key]">{{ c.name }}</option>
            </template>
            <template v-else-if="field.options === 'hobbies'">
              <option v-for="(c, ci) of hobbies" :key="ci" :selected="c == pax[field.key]">{{ c }}</option>
            </template>
            <template v-else-if="field.options === 'tripReasons'">
              <option v-for="(c, ci) of tripReasons" :key="ci" :selected="c == pax[field.key]">{{ c }}</option>
            </template>
            <template v-else-if="field.options === 'static'">
              <option v-for="(c, ci) of field.optionsStatic" :key="ci" :value="c.value" :selected="c.value == pax[field.key]">{{ c.label }}</option>
            </template>
          </select>
        </template>

        <template v-else>
          <input :type="field.type || 'text'" v-model="pax[field.key]" @change="update"
            :class="{'error': errors[field.key], 'readonly-field': isDisabled}"
            :disabled="isDisabled"
            :maxlength="field.maxlength || null"
            :placeholder="field.placeholder ? tr(field.placeholder) : null"
            class="bg-light-gray border border-[#ADAEAE] text-gray text-sm rounded-lg focus:border-[#ADAEAE] focus:ring-transparent outline-none block w-full p-3" />
        </template>
      </div>
    </template>
  </div>
  <div v-if="!isGroup || !pax.paxId" class="w-full mt-6 pl-4 border-l-4 border-primary">
    <h3 class="font-secondary text-xl mb-2">{{ tr('Medical Coverage / Travel Insurance') }}</h3>
    <p class="text-gray mb-4">{{ tr('TRAVEL_INSURANCE_HELP') }}</p>
    <div class="w-full grid md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-4 items-end">
      <div class="mb-5">
        <label class="block mb-2 text-sm font-medium text-gray uppercase">{{ tr('Insurance company') }}</label>
        <input type="text" v-model="pax.insuranceCompany" @input="updateInsurance" :disabled="isDisabled" :class="{'readonly-field': isDisabled}" class="bg-light-gray border border-[#ADAEAE] text-gray text-sm rounded-lg focus:border-[#ADAEAE] focus:ring-transparent outline-none block w-full p-3" />
      </div>
      <div class="mb-5">
        <label class="block mb-2 text-sm font-medium text-gray uppercase">{{ tr('Policy number') }}</label>
        <input type="text" v-model="pax.insurancePolicyNumber" @input="updateInsurance" :disabled="isDisabled" :class="{'readonly-field': isDisabled}" class="bg-light-gray border border-[#ADAEAE] text-gray text-sm rounded-lg focus:border-[#ADAEAE] focus:ring-transparent outline-none block w-full p-3" />
      </div>
      <div class="mb-5">
        <label class="block mb-2 text-sm font-medium text-gray uppercase">{{ tr('Insurer emergency phone') }}</label>
        <input type="tel" v-model="pax.insuranceEmergencyPhone" @input="updateInsurance" :disabled="isDisabled" :class="{'readonly-field': isDisabled}" class="bg-light-gray border border-[#ADAEAE] text-gray text-sm rounded-lg focus:border-[#ADAEAE] focus:ring-transparent outline-none block w-full p-3" />
      </div>
    </div>
  </div>
  <div v-if="isGroup && !pax.paxId" class="w-full mt-6 pl-4 border-l-4 border-primary">
    <h3 class="font-secondary text-xl mb-4">{{ tr('International Flights') }}</h3>
    <div v-for="(flight, index) in pax.internationalFlights" :key="`pax-if-${index}`" class="mb-6">
      <BookingFormFlight :flight="flight" @update="updateFlight('internationalFlights', index, $event)" />
      <button v-if="pax.internationalFlights.length > 1" class="trash mt-2" @click.prevent="removeFlight('internationalFlights', index)">X</button>
    </div>
    <button class="py-2 px-4 border border-primary rounded-md text-sm text-primary" @click.prevent="addFlight('internationalFlights')">+ {{ tr('Add Flight') }}</button>

    <h3 class="font-secondary text-xl mt-8 mb-4">{{ tr('Domestic Flights') }}</h3>
    <div v-for="(flight, index) in pax.domesticFlights" :key="`pax-df-${index}`" class="mb-6">
      <BookingFormFlight :flight="flight" @update="updateFlight('domesticFlights', index, $event)" />
      <button v-if="pax.domesticFlights.length > 1" class="trash mt-2" @click.prevent="removeFlight('domesticFlights', index)">X</button>
    </div>
    <button class="py-2 px-4 border border-primary rounded-md text-sm text-primary" @click.prevent="addFlight('domesticFlights')">+ {{ tr('Add Flight') }}</button>
  </div>
</template>

<script>
import DatePartsSelect from '@/components/tools/DatePartsSelect.vue';
import BookingFormFlight from '@/components/bookingform/BookingFormFlight.vue';
let countriesJSON = require(`@/assets/world.json`);
export default {
  name: 'BookingFromPax',
  emits: ['updateData'],
  data() {
    return {
      countriesJSON: countriesJSON,
      pax: {},
      useFirstPassengerEmergency: false,
      hobbies: [
        this.tr('Historical'),
        this.tr('Cultural'),
        this.tr('Wine'),
        this.tr('Trekking'),
        this.tr('Fishing'),
        this.tr('Tango'),
        this.tr('Geography'),
        this.tr('Others'),
      ],
      tripReasons: [
        this.tr('Birthday / Anniversary'),
        this.tr('Honeymoon'),
        this.tr('Tourism'),
        this.tr('Adventure trips'),
        this.tr('Others'),
      ],
      limitSize: 10000000,
      emptyFlight: {
        airline: '', number: '', reservationCode: '', departureDate: '', arrivalDate: '',
        departureCity: '', arrivalCity: '', departureTime: '', arrivalTime: ''
      },
      fields: [
        {
          key: 'selected',
          label: 'Passanger Select',
          span: 2,
          required: true,
          special: 'paxSelect',
        },
        {
          key: 'name',
          label: 'Name (as passport)',
          type: 'text',
          span: 2,
          required: true,
        },
        {
          key: 'lastName',
          label: 'Last Name (As Passport)',
          type: 'text',
          span: 2,
          required: true,
        },
        {
          key: 'DOB',
          label: 'Date of birth',
          span: 3,
          required: true,
          special: 'date',
        },
        {
          key: 'gender',
          label: 'Gender',
          type: 'select',
          span: 2,
          required: false,
          id: 'gender',
          options: 'gender',
        },
        {
          key: 'nationality',
          label: 'Nationality',
          type: 'select',
          span: 2,
          required: true,
          id: 'countries',
          options: 'countries',
        },
        {
          key: 'passport',
          label: 'Valid Passport Number',
          type: 'text',
          span: 2,
          required: true,
        },
        {
          key: 'passportExpirationDate',
          label: 'Passport Expiration Date',
          span: 3,
          required: true,
          special: 'date',
          minYear: new Date().getFullYear(),
          maxYear: new Date().getFullYear() + 40,
        },
        {
          key: 'address',
          label: 'Address',
          type: 'text',
          span: 2,
          required: false,
        },
        {
          key: 'email',
          label: 'Email',
          type: 'email',
          span: 2,
          required: false,
        },
        {
          key: 'mobile',
          label: 'Cellphone',
          type: 'text',
          span: 2,
          required: true,
        },
        {
          key: 'tripReason',
          label: 'Main reason of your trip',
          type: 'select',
          span: 2,
          required: false,
          id: 'hobbies',
          options: 'tripReasons',
        },
        {
          key: 'allergies',
          label: 'Allergies',
          type: 'text',
          span: 2,
          required: false,
          maxlength: 60,
          placeholder: 'If yes, which?',
        },
        {
          key: 'medicalConditionsMedications',
          label: 'MEDICAL CONDITIONS / MEDICATIONS',
          type: 'text',
          span: 2,
          required: false,
          maxlength: 60,
          placeholder: 'If yes, which?',
        },
        {
          key: 'foodRestriction',
          label: 'Food Restriction',
          type: 'text',
          span: 2,
          required: false,
          maxlength: 60,
          placeholder: 'Celiac, vegetarian, etc.',
        },
        {
          key: 'hobbies',
          label: 'Hobbies',
          type: 'select',
          span: 2,
          required: false,
          id: 'hobbies',
          options: 'hobbies',
        },
        {
          label: 'Please upload an image of your Passport',
          span: 2,
          required: false,
          special: 'file',
          wrapperClass: 'col-span-2 sm:col-span-2',
          show: () => !this.isDisabled,
        },
      ],
      groupFields: [
        { key: 'selected', label: 'Passanger Select', span: 2, required: true, special: 'paxSelect' },
        { key: 'name', label: 'First Name', type: 'text', span: 2, required: true },
        { key: 'middleName', label: 'Middle Name', type: 'text', span: 2, required: true },
        { key: 'lastName', label: 'Last Name', type: 'text', span: 2, required: true },
        { key: 'nationality', label: 'Nationality', type: 'select', span: 2, required: true, id: 'countries', options: 'countries' },
        { key: 'DOB', label: 'Date of Birth', span: 3, required: true, special: 'date' },
        { key: 'passport', label: 'Passport', type: 'text', span: 2, required: true },
        { key: 'passportExpirationDate', label: 'Passport Expiration Date', span: 3, required: true, special: 'date', minYear: new Date().getFullYear(), maxYear: new Date().getFullYear() + 40 },
        { key: 'email', label: 'Email', type: 'email', span: 2, required: true },
        { key: 'mobile', label: 'Mobile', type: 'text', span: 2, required: true },
        { key: 'emergencyContact', label: 'Emergency Contact', type: 'text', span: 2, required: false },
        { key: 'allergies', label: 'Allergies', type: 'text', span: 2, required: false, maxlength: 60 },
        { key: 'foodRestriction', label: 'Food Restrictions', type: 'text', span: 2, required: false, maxlength: 60 },
        { key: 'medicalConditionsMedications', label: 'Medical Condition & Medication', type: 'text', span: 2, required: false, maxlength: 60 },
        { key: 'sharingRoomWith', label: 'Sharing Room With', type: 'text', span: 2, required: false },
        {
          key: 'typeOfBedding',
          label: 'Type of Bedding',
          type: 'select',
          span: 2,
          required: false,
          id: 'typeOfBedding',
          options: 'static',
          optionsStatic: [
            { value: 'Single', label: 'Single' },
            { value: 'Double', label: 'Double' },
            { value: 'Triple', label: 'Triple' },
            { value: 'Others', label: 'Others' },
          ],
        },
      ],
    }
  },
  components: {
    DatePartsSelect,
    BookingFormFlight
  },
  mounted () {
    this.pax = Object.assign({}, this.passenger);
    this.pax.internationalFlights = (this.passenger.internationalFlights || [this.emptyFlight]).map(f => ({ ...f }));
    this.pax.domesticFlights = (this.passenger.domesticFlights || [this.emptyFlight]).map(f => ({ ...f }));
  },
  props: {
    passenger: Object,
    errors: Object,
    paxNumber: Number,
    isDisabled: {
      type: Boolean,
      default: false
    },
    paxList: {
      type: Array,
      default: () => []
    },
    isFirstPassenger: {
      type: Boolean,
      default: false
    },
    firstEmergencyContact: {
      type: String,
      default: ''
    },
    isGroup: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    addFlight (collection) {
      this.pax[collection].push({ ...this.emptyFlight });
      this.update();
    },
    removeFlight (collection, index) {
      this.pax[collection].splice(index, 1);
      this.update();
    },
    updateFlight (collection, index, flight) {
      this.pax[collection].splice(index, 1, flight);
      this.update();
    },
    update () {
      if (!this.isDisabled) {
        this.$emit('updateData', this.pax, this.paxNumber);
      }
    },
    updateInsurance () {
      this.$emit('updateData', this.pax, this.paxNumber);
    },
    updateDOB (value) {
      if (!this.isDisabled) {
        if (value && typeof value === 'object') {
          return;
        }

        this.pax.DOB = value;

        this.update();
      }
    },
    toggleUseFirstPassengerEmergency () {
      if (this.useFirstPassengerEmergency) {
        this.pax.emergencyContact = this.firstEmergencyContact || '';
        this.update();
      } else {
        this.pax.emergencyContact = '';
        this.update();
      }
    },
    updatePassportExpirationDate (value) {
      if (!this.isDisabled) {
        if (value && typeof value === 'object') {
          return;
        }

        this.pax.passportExpirationDate = value;

        this.update();
      }
    },
    updateDateField (key, value) {
      if (!this.isDisabled) {
        if (value && typeof value === 'object') {
          return;
        }

        this.pax[key] = value;

        this.update();
      }
    },
    checkFileSize (file) {
      if (file.size > this.limitSize) {
        return false;
      }
      return true;
    },
    uploadPassport (event) {
      if (this.isDisabled) return;

      let input = event.target;
      if (input.files) {
        let err;
        for (let j = 0; j < input.files.length; j++) {
          if (!this.checkFileSize(input.files[j])) {
            err = true;
          }
        }
        if (err) {
          input.files = null;
          alert(`Maximum allowed file size ${this.limitSize / 1000000} Mb`);
        } else {
          this.pax.files = input.files;
          this.update();
        }
      }
    },
    tr (key) {
      return this.$root.tr(key);
    },
  },
  watch: {
    firstEmergencyContact (newValue) {
      if (this.useFirstPassengerEmergency && !this.isFirstPassenger) {
        this.pax.emergencyContact = newValue || '';
        this.update();
      }
    }
  },
  computed: {
    activeFields () {
      return this.isGroup ? this.groupFields : this.fields;
    }
  }
}
</script>
<style scoped>
a {
  cursor: pointer;
}

.error {
  border: 2px solid red;
}

.readonly-field {
  background-color: #f5f5f5;
  color: #666;
  cursor: not-allowed;
  opacity: 0.8;
}

</style>
